import clsx from "clsx"
import { useContext, useState } from "react"

import useHeightCSSVariable from "@hooks/useHeightCSSVariable"

import { faIcon } from "@utils/faIcon"

import { EmptyState, Popover, ViewSelector } from "@molecules"
import { ViewSelectorProps } from "@molecules/ViewSelector/ViewSelector.types"

import { ObjectsViewContext } from "@organisms/ObjectsView/ObjectsView.context"
import { ObjectsViewType } from "@organisms/ObjectsView/ObjectsView.types"
import useViewTypesProvider from "@organisms/ObjectsView/ViewTypesProvider/ViewTypesProvider.context"
import ViewBar from "@organisms/ViewBar/ViewBar"

import CardsViewMenu, { CARDS_VIEW_MENU_COMPONENT_IDS } from "./CardsViewMenu/CardsViewMenu"
import TableViewMenu, { TABLE_VIEW_MENU_COMPONENT_IDS } from "./TableViewMenu/TableViewMenu"
import TimelineViewMenu, { TIMELINE_VIEW_MENU_COMPONENT_IDS } from "./TimelineViewMenu/TimelineViewMenu"
import styles from "./ViewMenu.module.scss"
import { ViewTypeMenu } from "./ViewMenu.types"

// Mounted inside the viewport so the height registration runs when the popover opens, not when the trigger mounts.
function ViewMenuSelector(props: ViewSelectorProps) {
    const { viewTypes } = props

    // The selector sits above every view, so the views' bodies have to give up its height or the footer gets pushed
    // out of the popover on short screens.
    const viewSelectorRef = useHeightCSSVariable<HTMLDivElement>("--popover-viewport-chrome-height")

    return (
        <div ref={viewSelectorRef} className={styles.viewSelector}>
            <ViewSelector viewTypes={viewTypes} />
        </div>
    )
}

function UnsupportedViewMenu() {
    return (
        <Popover.View componentId="main">
            <Popover.Body>
                <EmptyState
                    variant="error"
                    icon={faIcon({ name: "circle-exclamation", style: "solid" })}
                    title="Hmm, something went wrong..."
                    supportText="The settings for this view couldn't be displayed."
                />
            </Popover.Body>
        </Popover.View>
    )
}

// Every view type with its own settings menu. A view type without an entry gets the unsupported state.
const VIEW_TYPE_MENUS: Partial<Record<ObjectsViewType, ViewTypeMenu>> = {
    table: { Menu: TableViewMenu, componentIds: TABLE_VIEW_MENU_COMPONENT_IDS },
    cards: { Menu: CardsViewMenu, componentIds: CARDS_VIEW_MENU_COMPONENT_IDS },
    timeline: { Menu: TimelineViewMenu, componentIds: TIMELINE_VIEW_MENU_COMPONENT_IDS },
}

const UNSUPPORTED_VIEW_TYPE_MENU: ViewTypeMenu = {
    Menu: UnsupportedViewMenu,
    componentIds: ["main"],
}

/**
 * The view settings trigger and popover for a list page.
 *
 * Owns the popover shell and the view selector, and fills the body with the menu for the active
 * view type so switching views from the selector swaps the settings in place.
 */
export default function ViewMenu() {
    const { viewTypes, activeViewType } = useViewTypesProvider()

    // The list provider owns the open state so the list edge states can open this menu too. The timeline
    // mounts the menu without a list provider and keeps the state to itself.
    const [isLocalViewMenuOpen, setIsLocalViewMenuOpen] = useState<boolean>(false)
    const objectsViewContext = useContext(ObjectsViewContext)
    const isViewMenuOpen = objectsViewContext?.isViewMenuOpen ?? isLocalViewMenuOpen
    const setIsViewMenuOpen = objectsViewContext?.setIsViewMenuOpen ?? setIsLocalViewMenuOpen

    const { Menu, componentIds } = VIEW_TYPE_MENUS[activeViewType] ?? UNSUPPORTED_VIEW_TYPE_MENU
    const shouldShowViewSelector = viewTypes.length > 1

    return (
        <Popover.Root isOpen={isViewMenuOpen} onOpenChange={setIsViewMenuOpen}>
            <Popover.Trigger>
                <ViewBar.ViewMenuButton isPressed={isViewMenuOpen} />
            </Popover.Trigger>
            <Popover.Viewport
                componentIds={componentIds}
                className={clsx(styles.root, { [styles.noViewSelector]: !shouldShowViewSelector })}
            >
                {shouldShowViewSelector && <ViewMenuSelector viewTypes={viewTypes} />}
                <div className={styles.menu}>
                    <Menu />
                </div>
            </Popover.Viewport>
        </Popover.Root>
    )
}
